import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { 
  FileText, 
  Clock, 
  CheckCircle, 
  AlertTriangle,
  ChevronRight,
  Calendar,
  Award,
  Loader2,
  Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  not_started: { label: 'Not Started', color: 'bg-gray-100 text-gray-700', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700', icon: FileText },
  submitted: { label: 'Submitted', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  late: { label: 'Late', color: 'bg-orange-100 text-orange-700', icon: AlertTriangle },
  graded: { label: 'Graded', color: 'bg-purple-100 text-purple-700', icon: Award },
  returned: { label: 'Returned for Revision', color: 'bg-red-100 text-red-700', icon: AlertTriangle }
};

export default function AssignmentsList() {
  const { token } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchAssignments = async () => {
      try {
        const response = await fetch(`${API_URL}/api/portal/assignments/my`, {
          credentials: 'include',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        
        if (response.ok) {
          const data = await response.json();
          setAssignments(data.assignments || []);
        }
      } catch (err) {
        console.error('Failed to fetch assignments:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAssignments();
  }, [token]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'No due date';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const isOverdue = (dateStr, status) => {
    if (!dateStr || ['submitted', 'graded'].includes(status)) return false;
    return new Date(dateStr) < new Date();
  };

  const filteredAssignments = filter === 'all' 
    ? assignments 
    : assignments.filter(a => a.status === filter);

  // Group by status for summary
  const statusCounts = assignments.reduce((acc, a) => {
    acc[a.status] = (acc[a.status] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A] mx-auto mb-4" />
          <p className="text-gray-500">Loading assignments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="assignments-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Assignments</h1>
          <p className="text-gray-500 mt-1">{assignments.length} assignments across your courses</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[180px]">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Assignments</SelectItem>
            <SelectItem value="not_started">Not Started</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="graded">Graded</SelectItem>
            <SelectItem value="late">Late/Overdue</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Status Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={`cursor-pointer transition-all ${filter === 'not_started' ? 'ring-2 ring-[#1E3A8A]' : ''}`}
              onClick={() => setFilter(filter === 'not_started' ? 'all' : 'not_started')}>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{statusCounts.not_started || 0}</div>
            <div className="text-sm text-gray-500">To Do</div>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer transition-all ${filter === 'submitted' ? 'ring-2 ring-[#1E3A8A]' : ''}`}
              onClick={() => setFilter(filter === 'submitted' ? 'all' : 'submitted')}>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{statusCounts.submitted || 0}</div>
            <div className="text-sm text-gray-500">Submitted</div>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer transition-all ${filter === 'graded' ? 'ring-2 ring-[#1E3A8A]' : ''}`}
              onClick={() => setFilter(filter === 'graded' ? 'all' : 'graded')}>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">{statusCounts.graded || 0}</div>
            <div className="text-sm text-gray-500">Graded</div>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer transition-all ${filter === 'late' ? 'ring-2 ring-[#1E3A8A]' : ''}`}
              onClick={() => setFilter(filter === 'late' ? 'all' : 'late')}>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-orange-600">{(statusCounts.late || 0) + assignments.filter(a => isOverdue(a.due_date, a.status)).length}</div>
            <div className="text-sm text-gray-500">Overdue</div>
          </CardContent>
        </Card>
      </div>

      {/* Assignments List */}
      {filteredAssignments.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-900 mb-2">No assignments found</h3>
            <p className="text-gray-500">
              {filter !== 'all' ? 'Try changing your filter.' : 'Assignments will appear here when instructors post them.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredAssignments.map((assignment) => {
            const config = statusConfig[assignment.status] || statusConfig.not_started;
            const StatusIcon = config.icon;
            const overdue = isOverdue(assignment.due_date, assignment.status);
            
            return (
              <Link key={assignment.assignment_id} to={`/portal/assignments/${assignment.assignment_id}`}>
                <Card className={`hover:shadow-md transition-all cursor-pointer ${overdue && assignment.status === 'not_started' ? 'border-orange-300' : ''}`}>
                  <CardContent className="p-4 lg:p-6">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                        overdue && assignment.status === 'not_started' 
                          ? 'bg-orange-100' 
                          : 'bg-[#1E3A8A]/10'
                      }`}>
                        <FileText className={`w-6 h-6 ${
                          overdue && assignment.status === 'not_started' 
                            ? 'text-orange-600' 
                            : 'text-[#1E3A8A]'
                        }`} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <Badge className={config.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {config.label}
                          </Badge>
                          <span className="text-xs text-gray-400">{assignment.course_code}</span>
                        </div>
                        
                        <h3 className="font-semibold text-gray-900 mb-1">{assignment.title}</h3>
                        <p className="text-sm text-gray-500 line-clamp-2">{assignment.description}</p>
                        
                        <div className="flex items-center gap-4 mt-3 text-sm">
                          <div className={`flex items-center gap-1 ${overdue && assignment.status === 'not_started' ? 'text-orange-600 font-medium' : 'text-gray-500'}`}>
                            <Calendar className="w-4 h-4" />
                            {overdue && assignment.status === 'not_started' ? 'Overdue: ' : 'Due: '}
                            {formatDate(assignment.due_date)}
                          </div>
                          <div className="flex items-center gap-1 text-gray-500">
                            <Award className="w-4 h-4" />
                            {assignment.score !== null ? (
                              <span className="text-purple-600 font-medium">
                                {assignment.score}/{assignment.points_possible} pts
                              </span>
                            ) : (
                              <span>{assignment.points_possible} pts</span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
